import React, { useState } from 'react';
import { useEventPresignedUrl } from '../../hooks/useEventPresignedUrl';
import type { Event } from '../../types';

interface EventThumbnailProps {
  event: Event;
  onClick?: () => void;
  className?: string;
}

/**
 * Event thumbnail component with automatic presigned URL handling
 * 
 * Features:
 * - Automatically fetches presigned URLs for private S3 images
 * - Caches URLs to reduce API calls
 * - Shows loading state while fetching
 * - Falls back to "No image" placeholder if media unavailable
 */
export const EventThumbnail: React.FC<EventThumbnailProps> = ({ event, onClick, className }) => {
  const directUrl = event.s3_url || event.event_snapshot_url;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  
  const { presignedUrl, isLoading } = useEventPresignedUrl(
    event.event_id,
    'snapshot',
    directUrl
  );

  // No image available
  if (!directUrl) {
    return (
      <div className={className || "w-16 h-12 bg-background-tertiary rounded flex items-center justify-center"}>
        <span className="text-xs text-text-muted">No image</span>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={className || "w-16 h-12 bg-background-tertiary rounded flex items-center justify-center"}>
        <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (failedUrl === presignedUrl) {
    return <div className={className || 'w-16 h-12 bg-background-tertiary rounded flex items-center justify-center'}>
      <span className="text-xs text-text-muted">Load failed</span>
    </div>;
  }

  // Display image with presigned URL
  return (
    <img
      src={presignedUrl}
      alt="Event thumbnail"
      className={className || "w-16 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"}
      onClick={onClick}
      onError={() => setFailedUrl(presignedUrl)}
    />
  );
};

export default EventThumbnail;
